import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CUR_NETWORK, NETWORK, getLanguage, getCountry, getContiAlias } from '@constants/index';
import { setDataByDate, setImgWebp } from '@utils/index';
import type { IContentItem, CategoryReqParams, ContentReqParams, ContiResData, IContiData, IBannerItem } from '@type/index';

const isLocal = CUR_NETWORK === NETWORK.OPDEV;
const localDataPath = '/ts/common/data';
const language = getLanguage();
const country = getCountry();

export const scheduleApi = createApi({
    reducerPath: 'scheduleApi',
    baseQuery: fetchBaseQuery({
        credentials: 'include' 
    }),
    endpoints: (builder) => ({
        getCategory: builder.query({
            query: ({ option }) => {
                const { apiDomain, productId, serverType } = option || {};
                let params: CategoryReqParams = { productId };

                if(serverType) {
                    params = {...params, serverType};
                }

                return {
                    url: isLocal ? `${localDataPath}/category.json` : `${apiDomain}/contentlist/category/group`,
                    params
                };
            }
        }),
        // api에서 순서 정렬, 종료 일자 지난 데이터 필터해서 내려주시기로 함
        getContent: builder.query({
            query: ({ option, categoryIdList, optionalCategoryIdList, searchStartDate, searchEndDate }) => {
                const { apiDomain, productId, serverType } = option || {};
                let params: ContentReqParams = { productId, country };

                if(Array.isArray(categoryIdList) && categoryIdList?.length > 0) {
                    params = {...params, categoryIdList: categoryIdList?.join(',')};
                }

                if(Array.isArray(optionalCategoryIdList) && optionalCategoryIdList?.length > 0) {
                    params = {...params, optionalCategoryIdList: optionalCategoryIdList?.join(',')}
                }

                Object.entries({serverType, searchStartDate, searchEndDate})?.forEach(entry => {
                    const key = entry[0];
                    const value = entry[1];
                    
                    if(value) {
                        params = {...params, [key]: value};
                    }
                });

                return {
                    url: isLocal ? `${localDataPath}/content.json` : `${apiDomain}/contentlist/data/group`,
                    params
                };
            }
        }),
        getContiContent: builder.query({
            query: ({ option }) => {
                const { serviceAlias, apiDomain, serverType } = option || {}; 
                const groupAlias = getContiAlias(serviceAlias, serverType);
                const lang = `${language}-${country?.toUpperCase()}`;
                
                return {
                    url: isLocal ? `${localDataPath}/conti.json` : `${apiDomain}/conti/getContentGroup`,
                    params: {
                        groupAlias,
                        service: serviceAlias,
                        lang
                    }
                };
            },
            transformResponse: (response: ContiResData[]) => {
                let newRes = {} as IContiData;

                if(Array.isArray(response) && response?.length > 0) {
                    response?.forEach(data => {
                        const isBanner = /-banner$/.test(data?.alias);
                        const isDescription = /-description$/.test(data?.alias);
                        const jsonData = data?.jsonData ? JSON.parse(data?.jsonData) : null;

                        if(isBanner) {
                            const data = jsonData?.data?.map((data: IBannerItem) => ({...data, image: setImgWebp(data?.image)}));

                            newRes['banner'] = {...jsonData, data};
                        } else if(isDescription) {
                            newRes['description'] = jsonData;
                        }
                    });
                }
                
                return newRes;
            }
        })
    })
});

export const {
    useGetCategoryQuery,
    useGetContentQuery,
    useGetContiContentQuery
} = scheduleApi;

